import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'

ChartJS.register(
  CategoryScale, LinearScale, PointElement,
  LineElement, Title, Tooltip, Legend, Filler
)

export default function Progress({ session }) {
  const [scores, setScores] = useState([])
  const [checkins, setCheckins] = useState([])
  const [severity, setSeverity] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeChart, setActiveChart] = useState('score')

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    setLoading(true)
    const uid = session.user.id

    const [{ data: scoreData }, { data: checkinData }, { data: severityData }] =
      await Promise.all([
        supabase.from('afib_scores').select('*').eq('user_id', uid).order('score_date', { ascending: true }),
        supabase.from('daily_checkins').select('*').eq('user_id', uid).order('checkin_date', { ascending: true }),
        supabase.from('afib_severity').select('*').eq('user_id', uid).order('recorded_at', { ascending: true })
      ])

    setScores(scoreData || [])
    setCheckins(checkinData || [])
    setSeverity(severityData || [])
    setLoading(false)
  }

  function formatDate(dateStr) {
    const d = new Date(dateStr + 'T00:00:00')
    return `${d.getMonth() + 1}/${d.getDate()}`
  }

  function avgLast(arr, key, days) {
    const recent = arr.slice(-days)
    const vals = recent.map(r => r[key]).filter(v => v !== null && v !== undefined)
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '--'
  }

  const scoreLabels = scores.map(s => formatDate(s.score_date))
  const scoreValues = scores.map(s => s.scaled_score)

  const firstScore = scores.length > 0 ? scores[0].scaled_score : null
  const latestScore = scores.length > 0 ? scores[scores.length - 1].scaled_score : null
  const scoreChange = firstScore !== null && latestScore !== null ? latestScore - firstScore : null

  const exerciseAvg = avgLast(checkins, 'exercise_minutes', 7)
  const sleepAvg = avgLast(checkins, 'sleep_hours', 7)

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `Score: ${ctx.parsed.y}`
        }
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          font: { size: 11 }
        },
        grid: { color: '#f0f0f0' }
      },
      x: {
        ticks: { font: { size: 11 } },
        grid: { display: false }
      }
    }
  }

  const scoreChartData = {
    labels: scoreLabels,
    datasets: [{
      data: scoreValues,
      borderColor: '#185FA5',
      backgroundColor: 'rgba(24, 95, 165, 0.08)',
      borderWidth: 2.5,
      pointBackgroundColor: '#185FA5',
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.3,
      fill: true
    }]
  }

  const exerciseData = {
    labels: checkins.map(c => formatDate(c.checkin_date)),
    datasets: [{
      data: checkins.map(c => c.exercise_minutes || 0),
      borderColor: '#1D9E75',
      backgroundColor: 'rgba(29, 158, 117, 0.08)',
      borderWidth: 2.5,
      pointBackgroundColor: '#1D9E75',
      pointRadius: 4,
      tension: 0.3,
      fill: true
    }]
  }

  const sleepData = {
    labels: checkins.map(c => formatDate(c.checkin_date)),
    datasets: [{
      data: checkins.map(c => c.sleep_hours || 0),
      borderColor: '#534AB7',
      backgroundColor: 'rgba(83, 74, 183, 0.08)',
      borderWidth: 2.5,
      pointBackgroundColor: '#534AB7',
      pointRadius: 4,
      tension: 0.3,
      fill: true
    }]
  }

  const sleepOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        min: 0,
        max: 12,
        ticks: { stepSize: 2, font: { size: 11 } },
        grid: { color: '#f0f0f0' }
      },
      x: chartOptions.scales.x
    },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.parsed.y}h` }
      }
    }
  }

  const exerciseOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        min: 0,
        ticks: { font: { size: 11 } },
        grid: { color: '#f0f0f0' }
      },
      x: chartOptions.scales.x
    },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.parsed.y} min` }
      }
    }
  }

  const severityTotals = severity.map(s => ({
    date: s.recorded_at,
    type: s.assessment_type,
    total: s.frequency_score + s.duration_score + s.severity_score + s.symptom_score + s.wellbeing_score
  }))

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', fontFamily: 'Arial' }}>
      <p style={{ color: '#185FA5' }}>Loading your progress...</p>
    </div>
  )

  if (scores.length === 0) return (
    <div style={{ background: 'white', borderRadius: 12, padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📈</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#2C2C2C', marginBottom: 8 }}>No data yet</div>
      <div style={{ fontSize: 13, color: '#666' }}>Complete a few daily check-ins to see your progress charts.</div>
    </div>
  )

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Latest score</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#185FA5' }}>{latestScore}</div>
          <div style={{ fontSize: 10, color: '#888' }}>out of 100</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Change since start</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: scoreChange > 0 ? '#085041' : scoreChange < 0 ? '#A32D2D' : '#888' }}>
            {scoreChange > 0 ? '+' : ''}{scoreChange}
          </div>
          <div style={{ fontSize: 10, color: '#888' }}>points</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Avg exercise</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#1D9E75' }}>{exerciseAvg}</div>
          <div style={{ fontSize: 10, color: '#888' }}>min/day (7 days)</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Avg sleep</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#534AB7' }}>{sleepAvg}</div>
          <div style={{ fontSize: 10, color: '#888' }}>hours/night (7 days)</div>
        </div>
      </div>

      {/* Chart selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {[['score','📊 AFib score'],['exercise','🏃 Exercise'],['sleep','😴 Sleep'],['severity','😟 Severity']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveChart(key)} style={{
            padding: '6px 12px', borderRadius: 20, border: 'none', fontSize: 12,
            cursor: 'pointer', fontWeight: activeChart === key ? 700 : 400,
            background: activeChart === key ? '#185FA5' : 'white',
            color: activeChart === key ? 'white' : '#555'
          }}>{label}</button>
        ))}
      </div>

      {/* AFib Score Chart */}
      {activeChart === 'score' && (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C', marginBottom: 4 }}>AFib Control Score</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>{scores.length} days tracked</div>
          <div style={{ height: 200 }}>
            <Line data={scoreChartData} options={chartOptions} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 11, color: '#888' }}>
            <span>Start: <strong style={{ color: '#2C2C2C' }}>{firstScore}</strong></span>
            <span>Latest: <strong style={{ color: '#185FA5' }}>{latestScore}</strong></span>
            <span>Change: <strong style={{ color: scoreChange >= 0 ? '#085041' : '#A32D2D' }}>{scoreChange >= 0 ? '+' : ''}{scoreChange}</strong></span>
          </div>
        </div>
      )}

      {/* Exercise Chart */}
      {activeChart === 'exercise' && (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C', marginBottom: 4 }}>Exercise minutes per day</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Goal: 30+ minutes daily</div>
          <div style={{ height: 200 }}>
            <Line data={exerciseData} options={exerciseOptions} />
          </div>
          <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            background: parseFloat(exerciseAvg) >= 30 ? '#E1F5EE' : '#FAEEDA',
            color: parseFloat(exerciseAvg) >= 30 ? '#085041' : '#633806'
          }}>
            {parseFloat(exerciseAvg) >= 30 ? '✅ Averaging 30+ min/day — great work!' : `⚠️ Averaging ${exerciseAvg} min/day — try to reach 30 min`}
          </div>
        </div>
      )}

      {/* Sleep Chart */}
      {activeChart === 'sleep' && (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C', marginBottom: 4 }}>Sleep hours per night</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Goal: 7–8 hours nightly</div>
          <div style={{ height: 200 }}>
            <Line data={sleepData} options={sleepOptions} />
          </div>
          <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
            background: parseFloat(sleepAvg) >= 7 && parseFloat(sleepAvg) <= 8 ? '#E1F5EE' : '#FAEEDA',
            color: parseFloat(sleepAvg) >= 7 && parseFloat(sleepAvg) <= 8 ? '#085041' : '#633806'
          }}>
            {parseFloat(sleepAvg) >= 7 && parseFloat(sleepAvg) <= 8
              ? `✅ Averaging ${sleepAvg}h — right in the target range!`
              : `⚠️ Averaging ${sleepAvg}h — target is 7–8 hours nightly`}
          </div>
        </div>
      )}

      {/* Severity Chart */}
      {activeChart === 'severity' && (
        <div style={{ background: 'white', borderRadius: 12, padding: 16, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C', marginBottom: 4 }}>AFib Severity Scale</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Lower score = fewer symptoms</div>
          {severityTotals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#888', fontSize: 13 }}>
              No severity assessments yet. Complete one in the 😊 tab.
            </div>
          ) : (
            severityTotals.map((s, i) => {
              const pct = Math.round((s.total / 50) * 100)
              const color = s.total <= 15 ? '#085041' : s.total <= 25 ? '#633806' : '#A32D2D'
              const bg = s.total <= 15 ? '#E1F5EE' : s.total <= 25 ? '#FAEEDA' : '#FCEBEB'
              return (
                <div key={i} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: i < severityTotals.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, background: bg, color, padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>
                      {s.type}
                    </span>
                    <span style={{ fontSize: 12, color: '#888' }}>{formatDate(s.date)}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color }}>{s.total}/50</span>
                  </div>
                  <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4 }}>
                    <div style={{ width: `${pct}%`, height: 8, background: color, borderRadius: 4 }} />
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      <div style={{ textAlign: 'center', fontSize: 11, color: '#888', marginTop: 8 }}>
        Data from {scores.length} check-ins • Last updated {scores.length > 0 ? formatDate(scores[scores.length - 1].score_date) : '--'}
      </div>
    </div>
  )
}